import React, { useState } from 'react';
import { useAuth } from '../context/shared/AuthContext';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiService from '../services/shared/apiService';
import PageContainer from '../components/common/PageContainer';
import PageSection from '../components/common/PageSection';

interface Group {
  id: string;
  title: string;
  topic: string;
  description: string;
  symbolicTags: string[];
  memberCount: number;
  isModerated: boolean;
  createdAt: string;
  moderatorId: string;
  moderatorName: string;
  isJoined: boolean;
  isPending: boolean;
}

interface GroupPost {
  id: string;
  authorId: string;
  authorName: string;
  content: string;
  symbolicTags: string[];
  isAnonymous: boolean;
  createdAt: string;
  likes: number;
}

const Groups: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [newPost, setNewPost] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);

  // Fetch available groups
  const { data: groups, isLoading: groupsLoading } = useQuery({
    queryKey: ['groups'],
    queryFn: () => apiService.makeRequest('/api/groups'),
    enabled: !!user?.uid,
  });

  // Fetch group posts when a group is selected
  const { data: groupPosts, isLoading: postsLoading } = useQuery({
    queryKey: ['group-posts', selectedGroup?.id],
    queryFn: () => apiService.makeRequest(`/api/groups/${selectedGroup?.id}/posts`),
    enabled: !!selectedGroup?.id,
  });

  // Join group mutation
  const joinGroupMutation = useMutation({
    mutationFn: async (groupId: string) => {
      return apiService.makeRequest(`/api/groups/${groupId}/join`, {
        method: 'POST'
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
    },
  });

  // Create post mutation
  const createPostMutation = useMutation({
    mutationFn: async (postData: { content: string; isAnonymous: boolean }) => {
      return apiService.makeRequest(`/api/groups/${selectedGroup?.id}/posts`, {
        method: 'POST',
        data: postData
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group-posts', selectedGroup?.id] });
      setNewPost('');
      setIsAnonymous(false);
    },
  });

  const handleJoinGroup = (groupId: string) => {
    joinGroupMutation.mutate(groupId);
  };

  const handleCreatePost = () => {
    if (!newPost.trim() || !selectedGroup) return;
    
    createPostMutation.mutate({
      content: newPost.trim(),
      isAnonymous
    });
  };

  const getTagEmoji = (tag: string) => {
    const emojiMap: Record<string, string> = {
      hope: '✨', fear: '😰', exhaustion: '😴', anxiety: '😰', joy: '😊',
      growth: '🌱', confidence: '💪', energy: '⚡', clarity: '✨',
      gratitude: '🙏', peace: '🕊️', strength: '💪', resilience: '🛡️',
      healing: '🩹', sadness: '😢', anger: '😠', fatigue: '😴',
      confusion: '😕', overwhelm: '😵', isolation: '🏝️', shame: '😔',
      hopelessness: '😞', inertia: '🛑', reflection: '🤔', uncertainty: '❓',
      questioning: '🤷', rest: '😌', sleep: '😴', focus: '🎯',
      scatter: '💫', connection: '🤝', lonely: '🥺'
    };
    return emojiMap[tag] || '📝';
  };

  const getTagColor = (tag: string) => {
    const positiveTags = ['hope', 'joy', 'growth', 'confidence', 'energy', 'clarity', 'gratitude', 'peace', 'strength', 'resilience', 'healing'];
    const negativeTags = ['fear', 'anxiety', 'sadness', 'anger', 'fatigue', 'confusion', 'overwhelm', 'isolation', 'shame', 'hopelessness', 'inertia', 'exhaustion'];
    const neutralTags = ['reflection', 'uncertainty', 'questioning', 'rest', 'sleep', 'focus', 'scatter', 'connection', 'lonely'];

    if (positiveTags.includes(tag)) return 'bg-green-100 text-green-800';
    if (negativeTags.includes(tag)) return 'bg-red-100 text-red-800';
    if (neutralTags.includes(tag)) return 'bg-blue-100 text-blue-800';
    return 'bg-gray-100 text-gray-800';
  };

  return (
    <PageContainer>
      <div className="flex justify-between items-center mb-6 px-6 pt-6">
        <h1 className="text-3xl font-bold">Support Groups</h1>
        <button
          onClick={() => setShowCreateGroup(true)}
          className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
        >
          Create Group
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Groups List */}
        <div className="lg:col-span-1">
          <PageSection title="Available Groups" emoji="👥">
            {groupsLoading ? (
              <div className="flex justify-center items-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              </div>
            ) : groups?.groups?.length > 0 ? (
              <div className="space-y-4">
                {groups.groups.map((group: Group) => (
                  <div
                    key={group.id}
                    className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                      selectedGroup?.id === group.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setSelectedGroup(group)}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-semibold">{group.title}</h3>
                      <span className="text-sm text-gray-500">{group.memberCount} members</span>
                    </div>
                    <p className="text-sm text-gray-600  mb-3">
                      {group.description}
                    </p>
                    <div className="flex flex-wrap gap-1 mb-3">
                      {group.symbolicTags.slice(0, 3).map(tag => (
                        <span
                          key={tag}
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getTagColor(tag)}`}
                        >
                          <span className="mr-1">{getTagEmoji(tag)}</span>
                          {tag}
                        </span>
                      ))}
                      {group.symbolicTags.length > 3 && (
                        <span className="text-xs text-gray-500">
                          +{group.symbolicTags.length - 3} more
                        </span>
                      )}
                    </div>
                    {!group.isJoined && !group.isPending && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleJoinGroup(group.id);
                        }}
                        className="w-full px-3 py-1 bg-green-500 hover:bg-green-600 text-white rounded text-sm transition-colors"
                      >
                        Join Group
                      </button>
                    )}
                    {group.isPending && (
                      <span className="text-sm text-yellow-600 ">
                        Pending approval
                      </span>
                    )}
                    {group.isJoined && (
                      <span className="text-sm text-green-600 ">
                        ✓ Joined
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-3xl mb-3">👥</div>
                <p className="text-gray-500 ">
                  No support groups available yet.
                </p>
              </div>
            )}
          </PageSection>
        </div>

        {/* Group Content */}
        <div className="lg:col-span-2">
          {selectedGroup ? (
            <div className="space-y-6">
              {/* Group Header */}
              <PageSection title={selectedGroup.title} emoji="💬">
                <div className="mb-4">
                  <p className="text-gray-600  mb-3">
                    {selectedGroup.description}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {selectedGroup.symbolicTags.map(tag => (
                      <span
                        key={tag}
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getTagColor(tag)}`}
                      >
                        <span className="mr-1">{getTagEmoji(tag)}</span>
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </PageSection>

              {/* Posts */}
              <PageSection title="Group Discussion" emoji="💭">
                {selectedGroup.isJoined ? (
                  <div className="space-y-4">
                    {/* Create Post */}
                    <div className="bg-gray-50  p-4 rounded-lg">
                      <textarea
                        value={newPost}
                        onChange={(e) => setNewPost(e.target.value)}
                        placeholder="Share your thoughts... (symbolic tags will be automatically detected)"
                        className="w-full p-3 border border-gray-300  rounded-lg bg-white  resize-none h-24"
                      />
                      <div className="flex items-center justify-between mt-3">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={isAnonymous}
                            onChange={(e) => setIsAnonymous(e.target.checked)}
                            className="mr-2"
                          />
                          <span className="text-sm">Post anonymously</span>
                        </label>
                        <button
                          onClick={handleCreatePost}
                          disabled={!newPost.trim() || createPostMutation.isPending}
                          className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors disabled:opacity-50"
                        >
                          {createPostMutation.isPending ? 'Posting...' : 'Post'}
                        </button>
                      </div>
                    </div>

                    {/* Posts List */}
                    {postsLoading ? (
                      <div className="flex justify-center items-center h-32">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                      </div>
                    ) : groupPosts?.posts?.length > 0 ? (
                      <div className="space-y-4">
                        {groupPosts.posts.map((post: GroupPost) => (
                          <div key={post.id} className="bg-white  p-4 rounded-lg border border-gray-200 ">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center space-x-2">
                                <span className="font-medium">
                                  {post.isAnonymous ? 'Anonymous Member' : post.authorName}
                                </span>
                                {post.isAnonymous && (
                                  <span className="text-xs text-gray-500">(Anonymous)</span>
                                )}
                              </div>
                              <span className="text-sm text-gray-500">
                                {new Date(post.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                            <p className="text-gray-700  mb-3">
                              {post.content}
                            </p>
                            {post.symbolicTags.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {post.symbolicTags.map(tag => (
                                  <span
                                    key={tag}
                                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getTagColor(tag)}`}
                                  >
                                    <span className="mr-1">{getTagEmoji(tag)}</span>
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <div className="text-3xl mb-3">💭</div>
                        <p className="text-gray-500 ">
                          No posts yet. Be the first to share!
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="text-3xl mb-3">🔒</div>
                    <p className="text-gray-500  mb-4">
                      Join this group to participate in the discussion.
                    </p>
                    <button
                      onClick={() => handleJoinGroup(selectedGroup.id)}
                      className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
                    >
                      Join Group
                    </button>
                  </div>
                )}
              </PageSection>
            </div>
          ) : (
            <PageSection title="Select a Group" emoji="👥">
              <div className="text-center py-8">
                <div className="text-3xl mb-3">💬</div>
                <p className="text-gray-500 ">
                  Choose a support group from the list to start participating.
                </p>
              </div>
            </PageSection>
          )}
        </div>
      </div>

      {/* Create Group Modal */}
      {showCreateGroup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white  p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Create Support Group</h3>
            <p className="text-sm text-gray-500  mb-4">
              This feature is coming soon. For now, you can join existing groups.
            </p>
            <button
              onClick={() => setShowCreateGroup(false)}
              className="w-full px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </PageContainer>
  );
};

export default Groups; 